import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Target,
  Heart,
  Award,
  Users,
  Leaf,
  Shield,
  ThumbsUp,
  Clock,
  Phone,
  ArrowRight,
} from "lucide-react";

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: "Excellence",
      desc: "We strive for excellence in every service we provide, ensuring your lawn receives the best care possible.",
    },
    {
      icon: Heart,
      title: "Customer First",
      desc: "Your satisfaction is our priority. We listen, adapt, and go above and beyond to meet your needs.",
    },
    {
      icon: Leaf,
      title: "Sustainability",
      desc: "We use eco-friendly practices and products to care for your lawn while protecting the environment.",
    },
    {
      icon: Shield,
      title: "Integrity",
      desc: "Honest pricing, transparent communication, and reliable service you can trust.",
    },
  ];

  const stats = [
    { value: "10+", label: "Years Experience" },
    { value: "500+", label: "Happy Customers" },
    { value: "5,000+", label: "Lawns Serviced" },
    { value: "100%", label: "Satisfaction Guarantee" },
  ];

  const team = [
    {
      name: "Michael Torres",
      role: "Founder & Lead Specialist",
      desc: "15+ years of lawn care expertise",
    },
    {
      name: "David Chen",
      role: "Operations Manager",
      desc: "Ensures quality on every job",
    },
    {
      name: "James Wilson",
      role: "Senior Technician",
      desc: "Certified lawn care professional",
    },
    {
      name: "Carlos Rivera",
      role: "Customer Relations",
      desc: "Your point of contact",
    },
  ];

  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-forest-950 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-emerald-400 font-accent font-medium uppercase tracking-wider text-sm">About Us</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white mt-3 mb-6">
              Your Trusted Lawn Care Partner
            </h1>
            <p className="text-emerald-100/80 text-lg">
              Since our founding, Nexivas has been dedicated to transforming Houston lawns into beautiful, healthy outdoor spaces.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-emerald-600 font-accent font-medium uppercase tracking-wider text-sm">Our Story</span>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-forest-950 mt-3 mb-6">
                From Local Roots to Trusted Experts
              </h2>
              <div className="space-y-4 text-gray-600 text-lg">
                <p>
                  Nexivas was founded with a simple mission: to provide Houston homeowners and businesses with professional, reliable lawn care services that exceed expectations.
                </p>
                <p>
                  What started as a small operation has grown into a trusted name in the Houston area, serving hundreds of satisfied customers across the greater Houston metropolitan area.
                </p>
                <p>
                  Our team of trained professionals brings expertise, modern equipment, and a genuine passion for creating beautiful outdoor spaces. We treat every lawn as if it were our own.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1734303023481-7508b5c9f1ff?auto=format&fit=crop&w=800&q=80"
                alt="Nexivas team at work"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <p className="text-4xl md:text-5xl font-heading font-bold text-emerald-600 mb-2">{stat.value}</p>
                <p className="text-gray-600 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-emerald-600 font-accent font-medium uppercase tracking-wider text-sm">Our Values</span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-forest-950 mt-3 mb-6">
              What Drives Us Every Day
            </h2>
            <p className="text-gray-600 text-lg">
              Our core values guide everything we do, from how we treat our customers to how we care for your lawn.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-emerald-50/50 rounded-2xl p-8 text-center hover:bg-emerald-50 transition-colors"
                data-testid={`value-${index}`}
              >
                <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-forest-950 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="section-padding bg-emerald-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-emerald-600 font-accent font-medium uppercase tracking-wider text-sm">Our Team</span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-forest-950 mt-3 mb-6">
              The People Behind Nexivas
            </h2>
            <p className="text-gray-600 text-lg">
              Our experienced team is dedicated to providing exceptional service and creating beautiful lawns.
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 text-center shadow-sm hover:shadow-lg transition-shadow"
                data-testid={`team-${index}`}
              >
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-600 text-2xl font-bold">
                  {member.name.split(' ').map(n => n[0]).join('')}
                </div>
                <h3 className="text-lg font-heading font-semibold text-forest-950 mb-1">{member.name}</h3>
                <p className="text-emerald-600 font-medium text-sm mb-2">{member.role}</p>
                <p className="text-gray-500 text-sm">{member.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1555897209-d0bf7ab2d02f?auto=format&fit=crop&w=800&q=80"
                alt="Happy customer"
                className="rounded-2xl shadow-xl"
              />
            </div>
            
            <div>
              <span className="text-emerald-600 font-accent font-medium uppercase tracking-wider text-sm">Why Choose Us</span>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-forest-950 mt-3 mb-8">
                Customer Satisfaction is Our Priority
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <ThumbsUp className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-forest-950 mb-1">100% Satisfaction Guarantee</h4>
                    <p className="text-gray-600">If you're not happy with our work, we'll make it right—guaranteed.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-forest-950 mb-1">Reliable & Punctual</h4>
                    <p className="text-gray-600">We show up on time, every time. Dependability you can count on.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-forest-950 mb-1">Professional Excellence</h4>
                    <p className="text-gray-600">Trained technicians with the skills and equipment to do the job right.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-6">
            Ready to Experience the Nexivas Difference?
          </h2>
          <p className="text-emerald-100 text-lg mb-8">
            Join hundreds of satisfied Houston homeowners. Get your free quote today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button 
                className="bg-white text-emerald-600 hover:bg-emerald-50 rounded-full px-8 py-6 text-lg"
                data-testid="about-quote-btn"
              >
                Get Your Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+1-771-1871-987">
              <Button 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
                data-testid="about-call-btn"
              >
                <Phone className="mr-2 w-5 h-5" />
                +1-771-1871-987
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
